import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { Panel } from '../components/ui/Panel';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Zap, Bell, Plus, Pencil, Trash2, CheckCheck, Activity } from 'lucide-react';
import { formatNumber, formatCurrency, formatPercent, formatRelativeTime } from '../lib/utils';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useCampaignLookup, useOfferLookup, usePublisherLookup } from '../hooks/useLookups';

interface SystemHealth {
  postbackTotal24h: number;
  postbackMatched24h: number;
  postbackMatchRatePct: number | null;
  alerts24h: number;
  unreadAlerts: number;
  lastAlertAt: number | null;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type Metric = 'cr' | 'epc' | 'clicks' | 'conversions' | 'revenue' | 'bot_rate' | 'roi';

interface AutomationRule {
  id: number;
  name: string;
  scopeType: 'campaign' | 'offer' | 'publisher';
  scopeId: number | null;
  metric: Metric;
  operator: 'lt' | 'lte' | 'gt' | 'gte';
  threshold: number;
  windowHours: number;
  minSample: number;
  action: 'pause_campaign' | 'pause_offer' | 'alert' | 'webhook';
  webhookUrl: string | null;
  cooldownMinutes: number;
  isActive: boolean;
  lastFiredAt: number | null;
  notes: string | null;
}

interface AlertRow {
  id: number;
  ruleId: number | null;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  isRead: boolean;
  createdAt: number;
  metadata?: string;
}

const OP_LABEL: Record<string, string> = { lt: '<', lte: '≤', gt: '>', gte: '≥' };
const METRIC_LABEL: Record<Metric, string> = {
  cr: 'CR%',
  epc: 'EPC',
  clicks: 'clicks',
  conversions: 'conversions',
  revenue: 'revenue',
  bot_rate: 'bot rate %',
  roi: 'ROI%',
};

/** Kalimat kondisi manusiawi dari struktur rule — pola layar Stitch 5. */
function conditionSentence(r: AutomationRule): string {
  const thr =
    r.metric === 'revenue' || r.metric === 'epc'
      ? formatCurrency(r.threshold)
      : r.metric === 'cr' || r.metric === 'bot_rate'
        ? formatPercent(r.threshold, 1)
        : formatNumber(r.threshold);
  return `IF ${METRIC_LABEL[r.metric]} ${OP_LABEL[r.operator]} ${thr} over ${r.windowHours}h AND sample ≥ ${r.minSample}`;
}

function severityDotColor(s: string): string {
  if (s === 'critical') return 'bg-neon-red';
  if (s === 'warning') return 'bg-neon-yellow';
  return 'bg-text-tertiary';
}

function severityLabel(s: string): string {
  if (s === 'critical') return 'CRITICAL';
  if (s === 'warning') return 'WARNING';
  return 'INFO';
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AutomationPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'alerts' ? 'alerts' : 'rules';
  const [editFor, setEditFor] = useState<AutomationRule | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['automation-rules'] });
    qc.invalidateQueries({ queryKey: ['alerts'] });
    qc.invalidateQueries({ queryKey: ['alerts-unread'] });
  };

  const rules = useQuery({
    queryKey: ['automation-rules'],
    queryFn: () => apiClient.get<AutomationRule[]>('/api/v1/automation-rules/rules'),
    refetchInterval: 60_000,
  });

  const alertsQ = useQuery({
    queryKey: ['alerts'],
    queryFn: () =>
      apiClient.get<AlertRow[]>('/api/v1/alerts/alerts', { limit: 100 }),
    enabled: tab === 'alerts',
    refetchInterval: tab === 'alerts' ? 30_000 : false,
  });

  const unreadCount = useQuery({
    queryKey: ['alerts-unread-count'],
    queryFn: () => apiClient.get<{ count: number }>('/api/v1/alerts/unread-count'),
    refetchInterval: 30_000,
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiClient.patch(`/api/v1/automation-rules/rules/${id}`, { isActive }),
    onSuccess: invalidate,
  });

  const removeRule = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/automation-rules/rules/${id}`),
    onSuccess: invalidate,
  });

  const ack = useMutation({
    mutationFn: (id: number) => apiClient.post(`/api/v1/alerts/alerts/${id}/read`),
    onSuccess: invalidate,
  });

  const ackAll = useMutation({
    mutationFn: () => apiClient.post('/api/v1/alerts/alerts/read-all'),
    onSuccess: invalidate,
  });

  const handleDelete = async (r: AutomationRule) => {
    const res = await confirm({
      title: `Delete rule "${r.name}"?`,
      description: 'Rule yang dihapus berhenti dievaluasi permanen.',
      variant: 'danger',
      actions: [{ label: 'Delete', variant: 'danger', result: 'del', icon: <Trash2 size={14} /> }],
    });
    if (res === 'del') removeRule.mutate(r.id);
  };

  // Extract unique webhook URLs from rules for Webhook Targets panel
  const webhookTargets = Array.from(
    new Set(
      (rules.data ?? [])
        .filter((r) => r.action === 'webhook' && r.webhookUrl)
        .map((r) => r.webhookUrl!)
    )
  );

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
            <Zap size={22} className="text-neon-cyan" />
            AUTOMATION
          </h1>
          <p className="text-sm text-text-secondary font-mono mt-1">// evaluator ticks tiap 5 menit dari statistics_daily</p>
        </div>
        {tab === 'rules' && (
          <Button icon={<Plus size={14} />} onClick={() => setCreateOpen(true)} variant="neon" size="sm">
            + NEW RULE
          </Button>
        )}
        {tab === 'alerts' && (
          <Button
            variant="ghost"
            size="sm"
            icon={<CheckCheck size={14} />}
            onClick={() => ackAll.mutate()}
            loading={ackAll.isPending}
          >
            ACK ALL
          </Button>
        )}
      </div>

      {/* Tab pills */}
      <div className="inline-flex bg-bg-secondary border border-border-subtle rounded-sm p-1 gap-1">
        {(['rules', 'alerts'] as const).map((t) => {
          const badge = t === 'alerts' && unreadCount.data?.count ? (
            <span className="ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-neon-red text-white rounded-sm">
              {unreadCount.data.count}
            </span>
          ) : null;
          return (
            <button
              key={t}
              onClick={() => setParams(t === 'alerts' ? { tab: 'alerts' } : {})}
              className={`px-4 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm transition-all flex items-center gap-2 ${
                tab === t
                  ? 'bg-cyan-400/10 text-cyan-400 border border-cyan-400/30'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {t === 'rules' ? 'RULES' : 'ALERTS'}
              {badge}
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Main content (8 cols) */}
        <div className="col-span-12 lg:col-span-8">
          {tab === 'rules' && (
            <Panel level={1}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-subtle/60">
                      <th className="text-left py-3 px-4 text-[10px] font-display uppercase tracking-wider text-text-secondary">RULE</th>
                      <th className="text-left py-3 px-4 text-[10px] font-display uppercase tracking-wider text-text-secondary">SCOPE</th>
                      <th className="text-left py-3 px-4 text-[10px] font-display uppercase tracking-wider text-text-secondary">CONDITION</th>
                      <th className="text-left py-3 px-4 text-[10px] font-display uppercase tracking-wider text-text-secondary">ACTION</th>
                      <th className="text-right py-3 px-4 text-[10px] font-display uppercase tracking-wider text-text-secondary">COOLDOWN</th>
                      <th className="text-right py-3 px-4 text-[10px] font-display uppercase tracking-wider text-text-secondary">LAST FIRED</th>
                      <th className="text-right py-3 px-4 text-[10px] font-display uppercase tracking-wider text-text-secondary"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rules.data ?? []).map((r) => (
                      <tr key={r.id} className="border-b border-border-subtle/30 hover:bg-white/2 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-mono text-xs text-cyan-400">{r.name}</span>
                          {!r.isActive && (
                            <span className="ml-2 text-[9px] uppercase text-neon-yellow">paused</span>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono text-[11px] text-text-secondary">
                          {r.scopeType}
                          {r.scopeId != null ? `#${r.scopeId}` : ': all'}
                        </td>
                        <td className="py-3 px-4 font-mono text-[10px] text-text-tertiary max-w-[200px] truncate">
                          {conditionSentence(r)}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`text-[9px] uppercase px-1.5 py-0.5 rounded-sm border ${
                              r.action.startsWith('pause')
                                ? 'border-neon-magenta/40 text-neon-magenta'
                                : r.action === 'webhook'
                                  ? 'border-neon-purple/40 text-neon-purple'
                                  : 'border-cyan-400/40 text-cyan-400'
                            }`}
                          >
                            {r.action.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-xs text-text-secondary">
                          {r.cooldownMinutes}m
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-xs text-text-tertiary">
                          {r.lastFiredAt ? formatRelativeTime(r.lastFiredAt) : 'never'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <label className="inline-flex items-center cursor-pointer mr-2" title="Aktif/nonaktif">
                            <input
                              type="checkbox"
                              checked={r.isActive}
                              onChange={(e) => toggleActive.mutate({ id: r.id, isActive: e.target.checked })}
                              className="accent-cyan-400"
                            />
                          </label>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Pencil size={12} />}
                            onClick={() => setEditFor(r)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Trash2 size={12} />}
                            onClick={() => handleDelete(r)}
                            className="text-neon-red hover:opacity-70"
                          />
                        </td>
                      </tr>
                    ))}
                    {(rules.data ?? []).length === 0 && !rules.isLoading && (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-text-tertiary">
                          <Zap className="mx-auto mb-2 opacity-50" size={32} />
                          <p className="font-mono text-xs">
                            no rules yet — mis. "IF CR &lt; 1% over 24h → pause offer"
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          {tab === 'alerts' && (
            <Panel level={1}>
              <div className="divide-y divide-border-subtle/60">
                {(alertsQ.data ?? []).map((a) => (
                  <div
                    key={a.id}
                    className={`px-5 py-3 flex items-start gap-1.5 ${!a.isRead ? 'border-l-2 border-l-cyan-400' : ''}`}
                  >
                    <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${severityDotColor(a.severity)}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${a.isRead ? 'text-text-secondary' : 'text-text-primary'}`}>{a.message}</p>
                      <p className="text-[10px] font-mono text-text-tertiary mt-0.5">
                        {new Date(a.createdAt).toLocaleString()} · {severityLabel(a.severity)}
                        {a.ruleId ? ` · rule#${a.ruleId}` : ''}
                      </p>
                    </div>
                    {!a.isRead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => ack.mutate(a.id)}
                        loading={ack.isPending}
                      >
                        ACK
                      </Button>
                    )}
                  </div>
                ))}
                {(alertsQ.data ?? []).length === 0 && !alertsQ.isLoading && (
                  <div className="px-5 py-14 text-center font-mono text-xs text-text-tertiary">
                    <Bell className="mx-auto mb-2 opacity-50" size={28} />
                    no alerts — semua aman
                  </div>
                )}
              </div>
            </Panel>
          )}
        </div>

        {/* Right sidebar (4 cols) — Webhook Targets + System Health */}
        <div className="col-span-12 lg:col-span-4 space-y-4 sm:space-y-5">
          <Panel level={2} bracket={false}>
            <div className="p-4">
              <h3 className="font-display text-xs font-bold uppercase tracking-widest text-text-secondary mb-2 flex items-center gap-2">
                <Zap size={14} className="text-cyan-400" />
                WEBHOOK TARGETS
              </h3>
              {webhookTargets.length === 0 ? (
                <p className="text-xs font-mono text-text-tertiary">No webhook rules configured.</p>
              ) : (
                <ul className="space-y-1.5 font-mono text-xs">
                  {webhookTargets.map((url) => (
                    <li key={url} className="truncate text-text-secondary" title={url}>
                      {url}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Panel>

          <SystemHealthCard pollMult={1} />
        </div>
      </div>

      <RuleFormModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onSaved={invalidate}
      />
      {editFor && (
        <RuleFormModal
          open={!!editFor}
          mode="edit"
          initial={editFor}
          onClose={() => setEditFor(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}

// ─── SystemHealthCard (Stitch layar 5) ────────────────────────────────────────
// Sinyal nyata dari /stats/system-health (D1 counts, tanpa AE scan) + status
// optimizer dari smartlink campaign pertama. Queue depth / DLQ belum punya
// endpoint — sengaja tidak dipalsukan.
function SystemHealthCard({ pollMult }: { pollMult: number }) {
  const health = useQuery({
    queryKey: ['system-health'],
    queryFn: () => apiClient.get<SystemHealth>('/api/v1/stats/system-health'),
    refetchInterval: 60_000 * pollMult,
    retry: false,
  });

  const d = health.data;

  const matchRate = d?.postbackMatchRatePct;
  const matchColor =
    matchRate == null
      ? 'text-text-tertiary'
      : matchRate >= 95
        ? 'text-neon-green'
        : matchRate >= 80
          ? 'text-neon-yellow'
          : 'text-neon-red';

  return (
    <Panel level={2} bracket={false}>
      <div className="p-4">
        <h3 className="font-display text-xs font-bold uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-2">
          <Activity size={14} className={health.isError ? 'text-neon-red' : 'text-neon-green'} />
          SYSTEM HEALTH
          <span className="text-[9px] font-mono text-text-tertiary normal-case tracking-normal">24h</span>
        </h3>
        {health.isError ? (
          <p className="text-xs font-mono text-text-tertiary">health data unavailable</p>
        ) : (
          <div className="space-y-2 font-mono text-[11px]">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">POSTBACK SUCCESS</span>
              <span className={matchColor}>
                {matchRate == null ? '—' : formatPercent(matchRate, 1)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">POSTBACKS 24H</span>
              <span className="text-neon-cyan">{formatNumber(d?.postbackTotal24h ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">ALERTS 24H</span>
              <span className="text-text-primary">{formatNumber(d?.alerts24h ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">UNREAD ALERTS</span>
              <span className={(d?.unreadAlerts ?? 0) > 0 ? 'text-neon-red font-bold' : 'text-neon-green'}>
                {formatNumber(d?.unreadAlerts ?? 0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">EVALUATOR</span>
              <span className="text-neon-green">
                ACTIVE · tick */5m
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-secondary">LAST ALERT</span>
              <span className="text-text-tertiary">
                {d?.lastAlertAt ? formatRelativeTime(d.lastAlertAt) : 'never'}
              </span>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

// ─── RuleFormModal ────────────────────────────────────────────────────────────

function RuleFormModal({
  open,
  mode,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: AutomationRule | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const campaignLookup = useCampaignLookup();
  const offerLookup = useOfferLookup();
  const publisherLookup = usePublisherLookup();
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState(() => ({
    name: '',
    scopeType: 'campaign' as AutomationRule['scopeType'],
    scopeId: null as number | null,
    metric: 'cr' as Metric,
    operator: 'lt' as AutomationRule['operator'],
    threshold: 1,
    windowHours: 24,
    minSample: 100,
    action: 'alert' as AutomationRule['action'],
    webhookUrl: '',
    cooldownMinutes: 60,
    isActive: true,
    notes: '',
  }));

  useEffect(() => {
    if (open && mode === 'edit' && initial) {
      setForm({
        name: initial.name,
        scopeType: initial.scopeType,
        scopeId: initial.scopeId,
        metric: initial.metric,
        operator: initial.operator,
        threshold: initial.threshold,
        windowHours: initial.windowHours,
        minSample: initial.minSample,
        action: initial.action,
        webhookUrl: initial.webhookUrl ?? '',
        cooldownMinutes: initial.cooldownMinutes,
        isActive: initial.isActive,
        notes: initial.notes ?? '',
      });
    }
  }, [open, mode, initial]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        scopeId: form.scopeId == null ? null : Number(form.scopeId),
        webhookUrl: form.webhookUrl || undefined,
        notes: form.notes || undefined,
      };
      return mode === 'create'
        ? apiClient.post('/api/v1/automation-rules/rules', payload)
        : apiClient.patch(`/api/v1/automation-rules/rules/${initial!.id}`, payload);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError((e as Error).message),
  });

  // Scope options dari lookup maps (hook mem-expose .map, bukan list).
  const scopeOptions: Array<{ id: number; label: string }> =
    form.scopeType === 'campaign'
      ? [...campaignLookup.byId.values()].map((c) => ({ id: c.id, label: c.displayName }))
      : form.scopeType === 'offer'
        ? [...offerLookup.map.values()].map((o) => ({ id: o.id, label: o.name }))
        : [...publisherLookup.map.values()].map((p) => ({
            id: p.id,
            label: p.displayName ? `${p.subId} · ${p.displayName}` : p.subId,
          }));

  return (
    <Modal open={open} onClose={onClose} title={mode === 'edit' ? `Edit Rule: ${initial?.name}` : 'New Automation Rule'} size="lg">
      <form
        className="w-full space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <Input
          label="Nama rule"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="mis. cr-drop-smartlink-id"
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">Scope type</label>
            <select
              className="input-cyber text-xs"
              value={form.scopeType}
              onChange={(e) => setForm({ ...form, scopeType: e.target.value as AutomationRule['scopeType'], scopeId: null })}
            >
              <option value="campaign">campaign</option>
              <option value="offer">offer</option>
              <option value="publisher">publisher</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
              Target (kosong = semua)
            </label>
            <select
              className="input-cyber text-xs"
              value={form.scopeId ?? ''}
              onChange={(e) => setForm({ ...form, scopeId: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— all —</option>
              {scopeOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Kondisi: METRIC OPERATOR THRESHOLD dalam window */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">Metric</label>
            <select className="input-cyber text-xs" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value as Metric })}>
              {Object.entries(METRIC_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">Op</label>
            <select className="input-cyber text-xs" value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value as AutomationRule['operator'] })}>
              <option value="lt">&lt;</option>
              <option value="lte">≤</option>
              <option value="gt">&gt;</option>
              <option value="gte">≥</option>
            </select>
          </div>
          <Input
            label="Threshold"
            value={String(form.threshold)}
            onChange={(e) => setForm({ ...form, threshold: Number(e.target.value) })}
            type="number"
            step="any"
            required
          />
          <Input
            label="Window (jam)"
            value={String(form.windowHours)}
            onChange={(e) => setForm({ ...form, windowHours: Number(e.target.value) })}
            type="number"
            min={1}
            max={720}
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <Input
            label="Min sample"
            value={String(form.minSample)}
            onChange={(e) => setForm({ ...form, minSample: Number(e.target.value) })}
            type="number"
            min={0}
            required
          />
          <div>
            <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">Action</label>
            <select className="input-cyber text-xs" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value as AutomationRule['action'] })}>
              <option value="alert">alert</option>
              <option value="pause_campaign">pause campaign</option>
              <option value="pause_offer">pause offer</option>
              <option value="webhook">webhook</option>
            </select>
          </div>
          <Input
            label="Cooldown (menit)"
            value={String(form.cooldownMinutes)}
            onChange={(e) => setForm({ ...form, cooldownMinutes: Number(e.target.value) })}
            type="number"
            min={0}
            required
          />
        </div>

        {form.action === 'webhook' && (
          <Input
            label="Webhook URL"
            value={form.webhookUrl}
            onChange={(e) => setForm({ ...form, webhookUrl: e.target.value })}
            placeholder="https://…"
            required
          />
        )}

        {error && <p className="text-xs font-mono text-neon-red">{error}</p>}

        <div className="flex gap-2 justify-end pt-2 border-t border-border-subtle">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" loading={save.isPending}>
            {mode === 'edit' ? 'Save Changes' : 'Create Rule'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
